import axios from 'axios';
import { PortfolioData, ExchangeData } from '../types/portfolio';

// API URL for the Java backend - without the /api suffix since it's included in the endpoints
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080';

class PortfolioDataService {
  private data: PortfolioData = {};
  private userId: string | null = null;
  private isLoading: boolean = false;
  private lastLoadTime: Record<string, number> = {};
  private availableExchanges: string[] = [];

  // Get the current portfolio data
  getData(): PortfolioData {
    return this.data;
  }

  // Get portfolio data for a specific user
  async getPortfolioData(userId: string): Promise<PortfolioData> {
    if (!userId) {
      console.error('No user ID provided for getting portfolio data');
      return this.data;
    }

    // If data is already loaded and fresh, return it
    if (this.userId === userId && Object.keys(this.data).length > 0) {
      const allFresh = Object.keys(this.data).every(exchange => this.isDataFresh(exchange));
      if (allFresh) {
        console.log('Using cached portfolio data for user:', userId);
        return this.data;
      }
    }

    // Otherwise, load fresh data
    return this.loadAllData(userId);
  }

  // Set the user ID
  setUserId(userId: string): void {
    this.userId = userId;
  }

  // Check if data is fresh (less than 5 minutes old)
  isDataFresh(exchange?: string): boolean {
    const now = Date.now();
    
    // If no exchange is specified, check if any data is fresh
    if (!exchange) {
      // If no data has been loaded yet, it's not fresh
      if (Object.keys(this.lastLoadTime).length === 0) {
        return false;
      }
      
      // Check if all available exchanges have fresh data
      return Object.keys(this.data).every(exch => {
        const lastLoad = this.lastLoadTime[exch] || 0;
        const fiveMinutes = 5 * 60 * 1000;
        return (now - lastLoad) < fiveMinutes;
      });
    }
    
    // Check freshness for a specific exchange
    const lastLoad = this.lastLoadTime[exchange] || 0;
    const fiveMinutes = 5 * 60 * 1000;
    return (now - lastLoad) < fiveMinutes;
  }

  // Determine which exchanges the user has configured
  async determineAvailableExchanges(userId: string): Promise<string[]> {
    try {
      console.log(`Determining available exchanges for user: ${userId}`);
      this.availableExchanges = [];
      
      // Check for Binance US credentials
      try {
        const binanceusResponse = await axios.get(`${API_URL}/api/binanceus/health`, {
          headers: {
            'Content-Type': 'application/json',
            'X-User-Email': userId
          },
          params: {
            email: userId
          },
          timeout: 10000
        });
        
        if (binanceusResponse.data && binanceusResponse.data.credentialsFound) {
          console.log('Binance US credentials found');
          if (!this.availableExchanges.includes('binanceus')) {
            this.availableExchanges.push('binanceus');
          }
        }
      } catch (error) {
        console.warn('Error checking Binance US credentials:', error);
      }
      
      // Check for Kraken credentials
      try {
        const krakenResponse = await axios.get(`${API_URL}/api/kraken/health`, {
          headers: {
            'Content-Type': 'application/json',
            'X-User-Email': userId
          },
          params: {
            email: userId
          },
          timeout: 10000
        });
        
        if (krakenResponse.data && krakenResponse.data.credentialsFound) {
          console.log('Kraken credentials found');
          if (!this.availableExchanges.includes('kraken')) {
            this.availableExchanges.push('kraken');
          }
        }
      } catch (error) {
        console.warn('Error checking Kraken credentials:', error);
      }
      
      // Check for Coinbase credentials
      try {
        const coinbaseResponse = await axios.get(`${API_URL}/api/coinbase/portfolio/health`, {
          headers: {
            'Content-Type': 'application/json',
            'X-User-Email': userId
          },
          params: {
            email: userId
          },
          timeout: 10000
        });
        
        if (coinbaseResponse.data && coinbaseResponse.data.credentialsFound) {
          console.log('Coinbase credentials found');
          if (!this.availableExchanges.includes('coinbase')) {
            this.availableExchanges.push('coinbase');
          }
        }
      } catch (error) {
        console.warn('Error checking Coinbase credentials:', error);
      }

      // Check for Robinhood credentials
      try {
        const robinhoodResponse = await axios.get(`${API_URL}/api/admin/robinhood/health`, {
          headers: {
            'Content-Type': 'application/json',
            'X-User-Email': userId
          },
          params: {
            email: userId
          },
          timeout: 10000
        });
        if (robinhoodResponse.data && robinhoodResponse.data.credentialsFound) {
          console.log('Robinhood credentials found');
          if (!this.availableExchanges.includes('robinhood')) {
            this.availableExchanges.push('robinhood');
          }
        }
      } catch (error) {
        console.warn('Error checking Robinhood credentials:', error);
      }
      
      console.log('Available exchanges:', this.availableExchanges);
      return this.availableExchanges;
    } catch (error) {
      console.error('Error determining available exchanges:', error);
      return [];
    }
  }

  // Get available exchanges
  getAvailableExchanges(): string[] {
    return this.availableExchanges;
  }

  // Load data for all configured exchanges
  async loadAllData(userId: string): Promise<PortfolioData> {
    if (this.isLoading) {
      console.log('Already loading portfolio data, waiting...');
      // Wait for the current load to finish
      await new Promise(resolve => {
        const checkInterval = setInterval(() => {
          if (!this.isLoading) {
            clearInterval(checkInterval);
            resolve(true);
          }
        }, 100);
      });
      return this.data;
    }

    this.isLoading = true;
    this.userId = userId;
    
    try {
      console.log('Loading portfolio data for user:', userId);
      
      // First, determine which exchanges the user has configured
      await this.determineAvailableExchanges(userId);
      
      // Create a new data object
      const newData: PortfolioData = {};
      
      // Load data for each available exchange
      const loadPromises = this.availableExchanges.map(async (exchange) => {
        try {
          console.log(`Loading data for ${exchange}...`);
          
          // Use the appropriate endpoint for each exchange
          let endpoint = '';
          if (exchange === 'binanceus') {
            endpoint = `${API_URL}/api/binanceus/portfolio/data`;
          } else if (exchange === 'kraken') {
            endpoint = `${API_URL}/api/kraken/portfolio/data`;
          } else if (exchange === 'coinbase') {
            endpoint = `${API_URL}/api/coinbase/portfolio/data`;
          } else if (exchange === 'robinhood') {
            // Robinhood integration: expects a similar endpoint
            endpoint = `${API_URL}/api/robinhood/portfolio/data`;
          } else {
            console.warn(`Unknown exchange: ${exchange}`);
            return;
          }
          
          // Make the API call
          const response = await axios.get(endpoint, {
            headers: {
              'Content-Type': 'application/json',
              'X-User-Email': userId
            },
            params: {
              email: userId
            },
            timeout: 30000 // 30 seconds timeout for portfolio data
          });
          
          // Store the data
          newData[exchange] = response.data;
          
          // Update the last load time
          this.lastLoadTime[exchange] = Date.now();
          
          console.log(`Successfully loaded ${exchange} data`);
        } catch (error) {
          console.error(`Error loading ${exchange} data:`, error);
          // Store error information in the data, ensuring it matches the ExchangeData interface
          newData[exchange] = {
            error: `Failed to load ${exchange} data: ${error instanceof Error ? error.message : 'Unknown error'}`,
            connected: false,
            totalValue: 0,
            positions: []
          };
        }
      });
      
      // Wait for all data to be loaded
      await Promise.all(loadPromises);
      
      // Update the data
      this.data = newData;
      
      console.log('All portfolio data loaded:', Object.keys(this.data));
      return this.data;
    } catch (error) {
      console.error('Error loading portfolio data:', error);
      return this.data;
    } finally {
      this.isLoading = false;
    }
  }

  // Clear the cached data
  clearData(): void {
    this.data = {};
    this.lastLoadTime = {};
  }
}

export const portfolioDataService = new PortfolioDataService();
